import { computed, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { GoalActivity, IGoalResponse } from 'app/features/goal/models/goal.models';
import { ErrorCode } from 'app/shared/models/error.models';
import { PaginationMeta } from 'app/shared/models/pagination.models';
import { toGoalErrorCode } from '../utils/goal-error.utils';

export type GoalContext = {
  goal: number;
  username: string;
};

@Injectable()
export class GoalStore {
  readonly compactActivitiesLimit: number = 7;
  readonly expandedActivitiesPageSize: number = 30;

  readonly activitiesPage: WritableSignal<number> = signal<number>(0);
  readonly areActivitiesExpanded: WritableSignal<boolean> = signal<boolean>(false);
  readonly context: WritableSignal<GoalContext | null> = signal<GoalContext | null>(null);
  readonly error: WritableSignal<unknown | null> = signal<unknown | null>(null);
  readonly goalData: WritableSignal<IGoalResponse | null> = signal<IGoalResponse | null>(null);
  readonly isLoading: WritableSignal<boolean> = signal<boolean>(false);

  readonly goalErrorCode: Signal<ErrorCode | null> = computed<ErrorCode | null>(() => {
    const error: unknown | null = this.error();

    if (!error) return null;

    return toGoalErrorCode(error);
  });

  readonly paginationMeta: Signal<PaginationMeta | null> = computed<PaginationMeta | null>(
    () => this.goalData()?.meta ?? null
  );

  readonly activities: Signal<GoalActivity[]> = computed<GoalActivity[]>(() => {
    return this.goalData()?.data ?? [];
  });

  readonly visibleActivities: Signal<GoalActivity[]> = computed<GoalActivity[]>(() => {
    const activities: GoalActivity[] = this.activities();

    if (!this.areActivitiesExpanded()) {
      return activities.slice(0, this.compactActivitiesLimit);
    }

    return activities;
  });

  readonly shouldShowActivitiesToggle: Signal<boolean> = computed<boolean>(() => {
    const goalData: IGoalResponse | null = this.goalData();

    if (!goalData) return false;

    return (goalData.meta?.total ?? goalData.data.length) > this.compactActivitiesLimit;
  });

  resetForContext(context: GoalContext): void {
    this.activitiesPage.set(0);
    this.areActivitiesExpanded.set(false);
    this.error.set(null);
    this.goalData.set(null);
    this.isLoading.set(false);
    this.context.set(context);
  }
}
